import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ShoppingCart, Trash2, Cloud, Globe, ArrowRight, HardDrive, Users, Shield, Zap } from 'lucide-react';

function UnifiedCart() {
  const navigate = useNavigate();
  const { 
    cart, 
    isCartOpen, 
    setIsCartOpen, 
    removeCloud, 
    removeDomain, 
    getCartTotals, 
    getItemCount 
  } = useCart();

  const totals = getCartTotals();
  const itemCount = getItemCount();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  const goToCheckout = () => {
    setIsCartOpen(false);
    navigate('/warenkorb');
  };

  const continueShopping = () => {
    setIsCartOpen(false);
  };

  return (
    <>
      {/* Floating Cart Button - nur wenn Items im Warenkorb und nicht auf /warenkorb */}
      {itemCount > 0 && !isCartOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button 
            onClick={() => setIsCartOpen(true)} 
            size="lg" 
            className="rounded-full shadow-lg gap-2 px-6"
            data-testid="floating-cart-btn"
          >
            <ShoppingCart className="h-5 w-5" />
            <span>Warenkorb ({itemCount})</span>
            <Badge variant="secondary" className="ml-1">
              {formatPrice(totals.initialTotal)}
            </Badge>
          </Button>
        </div>
      )}

      {/* Cart Preview Modal - nur Vorschau, kein Checkout */}
      <Dialog open={isCartOpen} onOpenChange={setIsCartOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Warenkorb
            </DialogTitle>
          </DialogHeader>

          {itemCount === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">Dein Warenkorb ist leer</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={() => { setIsCartOpen(false); navigate('/preise'); }}>
                  Cloud konfigurieren
                </Button>
                <Button variant="outline" onClick={() => { setIsCartOpen(false); navigate('/domains'); }}>
                  Domains suchen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Cloud Item */}
              {cart.cloud && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Cloud className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">Wolkenhelden Cloud</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {cart.cloud.storage && (
                            <Badge variant="secondary" className="text-xs">
                              <HardDrive className="h-3 w-3 mr-1" />{cart.cloud.storage} GB
                            </Badge>
                          )}
                          {cart.cloud.collabora_users > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />{cart.cloud.collabora_users} Collabora
                            </Badge>
                          )}
                          {cart.cloud.antivirus && (
                            <Badge variant="secondary" className="text-xs">
                              <Shield className="h-3 w-3 mr-1" />Antivirus
                            </Badge>
                          )}
                          {cart.cloud.hp_backend && (
                            <Badge variant="secondary" className="text-xs">
                              <Zap className="h-3 w-3 mr-1" />Turbo
                            </Badge>
                          )}
                        </div>
                        {cart.cloud.domain && (
                          <p className="text-xs text-muted-foreground mt-2" data-testid="cart-cloud-domain">
                            <Globe className="h-3 w-3 inline mr-1" />
                            {cart.cloud.domain.name}
                            {cart.cloud.domain.type === 'purchase' && cart.cloud.domain.price > 0 && (
                              <span className="ml-1">({formatPrice(cart.cloud.domain.price)}/Jahr)</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{formatPrice(cart.cloud.pricing?.monthly)}/Mo</p>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={removeCloud}
                        className="h-8 w-8 text-destructive mt-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Domain Items */}
              {cart.domains?.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{item.domain}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.type === 'transfer' ? 'Transfer' : 'Registrierung'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">{formatPrice(item.price)}</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => removeDomain(item.id)}
                      className="h-8 w-8 text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <Separator />

              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Heute zu zahlen</span>
                  <span className="font-bold text-lg">{formatPrice(totals.initialTotal)}</span>
                </div>
                {totals.monthlyTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Dann monatlich</span>
                    <span>{formatPrice(totals.monthlyTotal)}</span>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={continueShopping} className="w-full sm:w-auto">
                  Weiter einkaufen
                </Button>
                <Button onClick={goToCheckout} className="w-full sm:w-auto">
                  Zur Kasse
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default UnifiedCart;
